'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Fragment, useEffect, useState } from 'react'

import Icon from '@/components/ui/icon'
import { CategoryFilters, CategoryProperty, FilteredCategory, SupplierFilter } from '@/types/category.type'

import FilterItemMobile from './FilterItemMobile'
import { cn } from '@/lib/utils'
import Button from '@/components/ui/Button'
import { useTranslations } from 'next-intl'

type FilterMobileProps = {
  brands: number[]
  prids: number[]
  filters: CategoryFilters
  filteredData: FilteredCategory['filtered']
  onClose: () => void
  stock: number[]
  totalProduct: number
  totalInStock: number
}

export default function FilterMobile({ filters, brands, prids, filteredData, onClose, stock, totalProduct, totalInStock }: FilterMobileProps) {
  const [suppliers, setSuppliers] = useState<SupplierFilter[]>(filters.suppliers)
  const [categoryProperties, setCategoryProperties] = useState<CategoryProperty[]>(filters.category_properties)
  const [brand, setBrand] = useState<number[]>(brands)
  const [prid, setPrid] = useState<number[]>(prids)
  const [filtered, setFiltered] = useState<FilteredCategory['filtered']>(filteredData) // các thuộc tính bị vô hiệu khi chọn bộ lọc
  // const t = useTranslations('product_detail')
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const tdetail= useTranslations('product_detail')
  const tcommon = useTranslations('common')
  const tp = useTranslations('product')
  // const stockOptions = [
  //   { id: 0, name: t('all') , hidden: false, slug: t('all')},
  //   { id: 1, name: t('has_stock'), hidden: false, slug: t('has_stock') },
  // ] as SupplierFilter[]

  const filteredCategories = filters.category_properties
    .map((category) => {
      // Lọc children có id tồn tại trong prid
      const filteredChildren = category.childrens.filter((child) => prids.includes(child.id))

      // Chỉ giữ lại mục cha nếu có ít nhất một children hợp lệ
      if (filteredChildren.length > 0) {
        return { ...category, childrens: filteredChildren }
      }
      return null // Nếu không có children hợp lệ, loại bỏ mục cha
    })
    .filter(Boolean)

  const filteredSuppliers = filters.suppliers.filter((supplier) => brands.includes(supplier.id))
  const handleDeleteAll = () => {
    const params = new URLSearchParams(searchParams)
    params.delete('brand')
    params.delete('prid')
    params.delete('in_stock')
    onClose()
    router.push(`${pathname}?${params.toString()}`)
  }

  const handleDeleteOne = (id: number, type: 'brand' | 'prid' | 'stock') => {
    const params = new URLSearchParams(searchParams)

    if (type === 'brand') {
      const newBrands = brands.filter((b) => b !== id)
      params.set('brand', newBrands.join(','))
    } else if (type === 'prid') {
      const newPrids = prids.filter((p) => p !== id)
      params.set('prid', newPrids.join(','))
    } else {
      params.delete('in_stock')
    }

    router.push(`${pathname}?${params.toString().replace(/%2C/g, ',')}`)
  }

  const isPridDisable = (id: number) => {
    if (filtered.length === 0) return false
    const pridExist = filtered.find((f) => f.id === id)

    return pridExist ? false : true
  }

  useEffect(() => {
    setSuppliers(filters.suppliers)
    setCategoryProperties(filters.category_properties)
    setBrand(brands)
    setPrid(prids)
    setFiltered(filteredData)
  }, [filters, brands, prids, filteredData])

  return (
    <div className="fixed inset-0 bg-[#eee] z-20 overflow-y-scroll no-scrollbar">
      <div className="sticky top-0 bg-white z-50">
        <div className="flex items-center justify-between px-4 py-2">
          <span className="text-primary" onClick={handleDeleteAll}>
            {tcommon('clear_all')}
          </span>
          <span className="font-bold absolute right-2/4 translate-x-2/4">{tp('filter_product')}</span>
          <div className="flex items-center" onClick={onClose}>
            <Icon icon="tgic icon-times" className="text-2xl" />
          </div>
        </div>

        {/* Select Filters: */}
        {(filteredSuppliers.length > 0 || filteredCategories.length > 0 || stock.length > 0) && (
          <div className="mb-6 space-y-2 p-2 border-t border-bordercolor">
            <div className="flex items-center gap-2 flex-wrap">
              {filteredSuppliers.map((sup) => (
                <div key={`supplier-${sup.id}`} className="inline-flex items-center gap-2 px-2 py-0.5 bg-[#e9f8ff] border border-bordercolor">
                  <span className="text-sm">{sup.name}</span>
                  <div className="text-[#b77777] cursor-pointer" onClick={() => handleDeleteOne(sup.id, 'brand')}>
                    <Icon icon="tgic icon-times-bold" />
                  </div>
                </div>
              ))}
              {filteredCategories.map((cat) => (
                <Fragment key={`catp-${cat?.id}`}>
                  {cat?.childrens.map((child) => (
                    <div key={`child-${child.id}`} className="inline-flex items-center gap-2 px-2 py-0.5 bg-[#e9f8ff] border border-bordercolor">
                      <span className="text-sm">{child.name}</span>
                      <div className="text-[#b77777] cursor-pointer" onClick={() => handleDeleteOne(child.id, 'prid')}>
                        <Icon icon="tgic icon-times-bold" />
                      </div>
                    </div>
                  ))}
                </Fragment>
              ))}
              {stock.length > 0 && (
                <div key={`stock-${stock[0]}`} className="inline-flex items-center gap-2 px-2 py-0.5 bg-[#e9f8ff] border border-bordercolor">

                  <span className="text-sm">{stock[0] === 1 ? 'Còn hàng' : 'Tất cả'}</span>
                  <div className="text-[#b77777] cursor-pointer" onClick={() => handleDeleteOne(stock[0], 'stock')}>
                    <Icon icon="tgic icon-times-bold" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <ul className="px-2 mt-[15px]">
        {/* brand */}
        <FilterItemMobile type="brand" suppliers={suppliers} categoryProperty={null} hasSelected={brand.length > 0} onClose={onClose} total={totalProduct} totalInStock={totalInStock} />
        {/* prid */}
        {categoryProperties.map((cp) => (
          <FilterItemMobile
            key={`category-p-${cp.id}`}
            type="prid"
            suppliers={null}
            categoryProperty={cp}
            prids={prid}
            setPrids={setPrid}
            hasSelected={cp.childrens.some((c) => prid.includes(c.id))}
            onClose={onClose}
            isPridDisable={isPridDisable}
            disableGlobal={cp.childrens.every((c) => isPridDisable(c.id))}
            total={totalProduct}
            totalInStock={totalInStock}
          />
        ))}
        {/* Stock */}
        <FilterItemMobile type="stock" suppliers={null} categoryProperty={null} hasSelected={stock.length > 0} onClose={onClose} total={totalProduct} totalInStock={totalInStock} />
      </ul>
      <div
        className={cn('sticky bottom-0 bg-white shadow-rounde z-10 p-2', {
          // 'h-0 overflow-hidden p-0': !isFilter,
          // 'h-auto p-2': isFilter,
        })}
        // onClick={() => setIsFilter(false)}
      >
        <Button full primary rounded onClick={onClose}>
          {tdetail('apply')}
        </Button>
      </div>
    </div>
  )
}
